import React from 'react';

// Deterministic hash
const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return Math.abs(h);
};

// Tough kit palettes (deep, high-contrast, bold combos)
const PALETTES = [
  { primary: '#C8102E', secondary: '#0A0A0C', accent: '#FFD700' }, // crimson/black/gold
  { primary: '#0A2540', secondary: '#FFFFFF', accent: '#FFD700' }, // navy/white/gold
  { primary: '#1B5E20', secondary: '#0A0A0C', accent: '#F0D07A' }, // forest/black/cream
  { primary: '#FF6B00', secondary: '#0A0A0C', accent: '#FFFFFF' }, // orange/black/white
  { primary: '#4A148C', secondary: '#FFD700', accent: '#FFFFFF' }, // royal purple/gold/white
  { primary: '#0A0A0C', secondary: '#FFD700', accent: '#C8102E' }, // black/gold/crimson
  { primary: '#0066CC', secondary: '#FFFFFF', accent: '#FF3B30' }, // royal blue/white/red
  { primary: '#7F0000', secondary: '#F0D07A', accent: '#FFFFFF' }, // burgundy/cream/white
  { primary: '#00695C', secondary: '#FFD700', accent: '#FFFFFF' }, // teal/gold/white
  { primary: '#1A1A1F', secondary: '#FF3B30', accent: '#FFD700' }, // jet/red/gold
  { primary: '#5D4037', secondary: '#FFD700', accent: '#FFFFFF' }, // bronze/gold/white
  { primary: '#37474F', secondary: '#F0D07A', accent: '#FF3B30' }, // gunmetal/cream/red
  { primary: '#01579B', secondary: '#FFD700', accent: '#0A0A0C' }, // deep azure/gold/black
  { primary: '#B71C1C', secondary: '#FFFFFF', accent: '#0A0A0C' }, // blood red/white/black
  { primary: '#33691E', secondary: '#FFFFFF', accent: '#FFD700' }, // moss/white/gold
  { primary: '#0D47A1', secondary: '#FF6B00', accent: '#FFFFFF' }, // ocean/orange/white
  { primary: '#212121', secondary: '#FFFFFF', accent: '#32D74B' }, // black/white/lime
  { primary: '#880E4F', secondary: '#FFD700', accent: '#0A0A0C' }, // wine/gold/black
  { primary: '#E65100', secondary: '#0A0A0C', accent: '#FFFFFF' }, // burnt orange/black/white
  { primary: '#3E2723', secondary: '#FFD700', accent: '#FF6B00' }, // mahogany/gold/orange
];

const SHAPES = ['shield-classic', 'shield-spike', 'crest-cut', 'octagon', 'diamond'];

// Mascot/symbol set — silhouette icons drawn to look "tough" in the crest center
const MASCOTS = [
  // Lion claw (3 slashes)
  (fg, accent) => (
    <g>
      <path d="M30 36 L42 60 L36 64 Z" fill={fg} opacity="0.95" />
      <path d="M44 32 L52 62 L46 64 Z" fill={fg} opacity="0.95" />
      <path d="M58 33 L62 61 L56 64 Z" fill={fg} opacity="0.95" />
      <circle cx="44" cy="30" r="2" fill={accent} />
    </g>
  ),
  // Crossed swords
  (fg, accent) => (
    <g stroke={fg} strokeWidth="3" strokeLinecap="round">
      <line x1="28" y1="28" x2="62" y2="68" />
      <line x1="62" y1="28" x2="28" y2="68" />
      <circle cx="45" cy="48" r="4" fill={accent} stroke="none" />
    </g>
  ),
  // Lightning bolt
  (fg) => (
    <path d="M52 24 L36 50 L46 52 L38 70 L60 44 L48 42 Z" fill={fg} stroke={fg} strokeWidth="1.5" strokeLinejoin="round" />
  ),
  // Wolf / canine head
  (fg, accent) => (
    <g>
      <path d="M30 32 L45 26 L60 32 L62 50 L55 64 L45 66 L35 64 L28 50 Z" fill={fg} />
      <circle cx="38" cy="44" r="2.2" fill={accent} />
      <circle cx="52" cy="44" r="2.2" fill={accent} />
      <path d="M42 56 L48 56 L45 60 Z" fill={accent} />
      <path d="M30 32 L26 24 L34 30 Z" fill={fg} />
      <path d="M60 32 L64 24 L56 30 Z" fill={fg} />
    </g>
  ),
  // Eagle wings
  (fg, accent) => (
    <g>
      <path d="M45 30 L20 45 L28 50 L20 55 L30 60 L45 50 Z" fill={fg} />
      <path d="M45 30 L70 45 L62 50 L70 55 L60 60 L45 50 Z" fill={fg} />
      <circle cx="45" cy="48" r="4" fill={accent} />
    </g>
  ),
  // Star + bolt fusion
  (fg, accent) => (
    <g>
      <polygon points="45,24 50,42 68,42 53,52 58,70 45,60 32,70 37,52 22,42 40,42" fill={fg} />
      <circle cx="45" cy="48" r="3" fill={accent} />
    </g>
  ),
  // Bull horns
  (fg, accent) => (
    <g>
      <path d="M28 38 Q22 30 30 28 Q40 32 44 44" fill="none" stroke={fg} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M62 38 Q68 30 60 28 Q50 32 46 44" fill="none" stroke={fg} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="45" cy="52" r="6" fill={fg} />
      <circle cx="45" cy="52" r="2" fill={accent} />
    </g>
  ),
  // Shield with stripes (heraldic)
  (fg, accent) => (
    <g>
      <path d="M30 32 L60 32 L60 52 Q60 64 45 70 Q30 64 30 52 Z" fill={fg} />
      <path d="M30 38 L60 38" stroke={accent} strokeWidth="2.5" />
      <path d="M30 48 L60 48" stroke={accent} strokeWidth="2.5" />
      <circle cx="45" cy="58" r="4" fill={accent} />
    </g>
  ),
];

const getInitials = (name) => {
  if (!name) return 'JW';
  const stripped = name.replace(/[^A-Za-z\s]/g, '');
  const words = stripped.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const founded = (seed) => 1880 + (seed % 140);

export const TeamLogo = ({ team, size = 80, className = '' }) => {
  if (!team) return null;
  const seed = hashString(team.city || team.name || '');
  const palette = PALETTES[seed % PALETTES.length];
  const shape = SHAPES[seed % SHAPES.length];
  const mascot = MASCOTS[seed % MASCOTS.length];
  const initials = getInitials(team.name);
  const yr = founded(seed);
  const id = `tlg-${team.id || initials}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 90 100"
      className={className}
      data-testid={`team-logo-${team.id}`}
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor={palette.primary} stopOpacity="1" />
          <stop offset="55%" stopColor={palette.primary} stopOpacity="0.92" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.65" />
        </linearGradient>
        <linearGradient id={`${id}-accent`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={palette.accent} />
          <stop offset="100%" stopColor={palette.secondary} />
        </linearGradient>
        <filter id={`${id}-shadow`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" />
          <feOffset dx="0" dy="0.8" result="off" />
          <feComponentTransfer><feFuncA type="linear" slope="0.6" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <pattern id={`${id}-grunge`} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill="transparent" />
          <circle cx="1" cy="1" r="0.5" fill="#000" opacity="0.18" />
          <circle cx="4" cy="3" r="0.4" fill="#000" opacity="0.14" />
          <circle cx="2" cy="5" r="0.3" fill="#fff" opacity="0.08" />
        </pattern>
      </defs>

      {/* Crest shape */}
      {shape === 'shield-classic' && (
        <path
          d="M45 4 L82 13 L82 50 Q82 76 45 96 Q8 76 8 50 L8 13 Z"
          fill={`url(#${id}-bg)`}
          stroke={palette.accent}
          strokeWidth="2.5"
          filter={`url(#${id}-shadow)`}
        />
      )}
      {shape === 'shield-spike' && (
        <path
          d="M45 2 L82 12 L78 50 L84 56 L74 70 Q60 90 45 96 Q30 90 16 70 L6 56 L12 50 L8 12 Z"
          fill={`url(#${id}-bg)`}
          stroke={palette.accent}
          strokeWidth="2.5"
          filter={`url(#${id}-shadow)`}
        />
      )}
      {shape === 'crest-cut' && (
        <path
          d="M14 6 L76 6 L86 22 L82 70 L72 84 Q58 98 45 98 Q32 98 18 84 L8 70 L4 22 Z"
          fill={`url(#${id}-bg)`}
          stroke={palette.accent}
          strokeWidth="2.5"
          filter={`url(#${id}-shadow)`}
        />
      )}
      {shape === 'octagon' && (
        <polygon
          points="22,6 68,6 84,24 84,72 68,94 22,94 6,72 6,24"
          fill={`url(#${id}-bg)`}
          stroke={palette.accent}
          strokeWidth="2.5"
          filter={`url(#${id}-shadow)`}
        />
      )}
      {shape === 'diamond' && (
        <polygon
          points="45,4 86,32 78,80 45,98 12,80 4,32"
          fill={`url(#${id}-bg)`}
          stroke={palette.accent}
          strokeWidth="2.5"
          filter={`url(#${id}-shadow)`}
        />
      )}

      {/* Grunge texture overlay */}
      <path d="M45 4 L82 13 L82 50 Q82 76 45 96 Q8 76 8 50 L8 13 Z" fill={`url(#${id}-grunge)`} opacity="0.7" />

      {/* Inner border */}
      <path
        d="M45 10 L76 18 L76 50 Q76 70 45 88 Q14 70 14 50 L14 18 Z"
        fill="none"
        stroke={palette.accent}
        strokeWidth="0.6"
        strokeDasharray="2 2"
        opacity="0.45"
      />

      {/* Top banner with year */}
      <rect x="22" y="14" width="46" height="9" fill={palette.accent} opacity="0.9" />
      <text
        x="45" y="20.5" textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="6" fontWeight="700"
        fill={palette.primary} letterSpacing="0.18em"
      >
        EST {yr}
      </text>

      {/* Mascot/symbol */}
      <g>{mascot(palette.secondary, palette.accent)}</g>

      {/* Bottom initials banner with diagonal slashes (grunge feel) */}
      <g>
        <rect x="6" y="74" width="78" height="18" fill="#000" opacity="0.55" />
        <path d="M6 74 L18 92" stroke={palette.accent} strokeWidth="0.8" opacity="0.6" />
        <path d="M84 74 L72 92" stroke={palette.accent} strokeWidth="0.8" opacity="0.6" />
        <text
          x="45" y="89" textAnchor="middle"
          fontFamily="'Bebas Neue', 'Barlow Condensed', Impact, sans-serif"
          fontSize="20" fontWeight="900" fill={palette.accent}
          style={{ letterSpacing: '4px' }}
        >
          {initials}
        </text>
      </g>

      {/* Top stud accent dots */}
      <circle cx="14" cy="14" r="1.4" fill={palette.accent} opacity="0.9" />
      <circle cx="76" cy="14" r="1.4" fill={palette.accent} opacity="0.9" />
    </svg>
  );
};
